import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useSound from 'use-sound';
import sounds from '../sounds.mp3';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeProfile } from '../sanitize';
import { normalizeEditorHtml, plainTextToEditorHtml } from '../editorHtml';
import { insertLineBreak } from '../editorLineBreak';

const soundMap = {
  msgSnd: [10300, 1000],
  error: [23700, 1000],
};

const PROFILE_MAX_LENGTH = 1024;

const FONT_SIZES = [
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7', value: '7' },
];

// Period-appropriate fonts (late 90s / early 2000s)
const FONTS = [
  'Times New Roman',
  'Arial',
  'Comic Sans MS',
  'Courier New',
  'Georgia',
  'Impact',
  'Tahoma',
  'Trebuchet MS',
  'Verdana',
  'Palatino Linotype',
  'Lucida Console',
  'Garamond',
];

// Windows 98 color picker — 48 standard colors
const WIN98_COLORS = [
  '#FF0000', '#FFFF00', '#00FF00', '#00FFFF', '#0000FF', '#FF00FF', '#FFFFFF', '#000000',
  '#800000', '#808000', '#008000', '#008080', '#000080', '#800080', '#C0C0C0', '#808080',
  '#FF8080', '#FFFF80', '#80FF80', '#80FFFF', '#8080FF', '#FF80FF', '#FFC0C0', '#404040',
  '#804040', '#808040', '#408040', '#408080', '#404080', '#804080', '#C0C0FF', '#200000',
  '#FF4040', '#FFFF40', '#40FF40', '#40FFFF', '#4040FF', '#FF40FF', '#C08080', '#600000',
  '#C00000', '#C0C000', '#00C000', '#00C0C0', '#0000C0', '#C000C0', '#FFC080', '#A0A0A0',
];

function ColorPalette({ onSelect, onClose, title }) {
  return (
    <div className="color-palette-popup window" style={{ position: 'absolute', zIndex: 1000, top: '100%', left: 0, width: 180 }}>
      <div className="title-bar" style={{ cursor: 'default' }}>
        <span className="title-bar-text"><span style={{ marginLeft: 0 }}>{title}</span></span>
        <div className="title-bar-controls">
          <button aria-label="Close" onClick={onClose}></button>
        </div>
      </div>
      <div className="window-body" style={{ padding: 4 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 1 }}>
          {WIN98_COLORS.map((color) => (
            <div
              key={color}
              role="button"
              tabIndex={0}
              onClick={() => { onSelect(color); onClose(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { onSelect(color); onClose(); } }}
              title={color}
              style={{
                width: 16, height: 16, backgroundColor: color, border: '1px solid #808080',
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ProfileEditor() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [bgcolor, setBgcolor] = useState('');
  const [htmlMode, setHtmlMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [showTextColor, setShowTextColor] = useState(false);
  const [showBgColor, setShowBgColor] = useState(false);
  const editorRef = useRef(null);

  const [play] = useSound(sounds, { interrupt: true, sprite: soundMap });

  // Load existing profile
  React.useEffect(() => {
    if (!user) return;
    fetch(`/api/profile/${encodeURIComponent(user.screen_name)}`)
      .then((r) => r.json())
      .then((data) => {
        setContent(data.profile || '');
        setBgcolor(data.bgcolor || '');
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [user]);

  // Sync contenteditable with state
  React.useEffect(() => {
    if (loaded && editorRef.current && !htmlMode) {
      editorRef.current.innerHTML = content;
    }
    // `content` is intentionally omitted: including it would rewrite innerHTML
    // on every keystroke and reset the cursor position.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, htmlMode]);

  // `content` is the source of truth rather than the live DOM: every edit path
  // syncs it, and reading the DOM during render is stale on mount, before the
  // effect above has written the loaded profile into it.
  const savedHtml = sanitizeProfile(content);
  const currentLength = savedHtml.length;
  const overLimit = currentLength > PROFILE_MAX_LENGTH;

  // Cleanup rewrites innerHTML, which moves the caret and clears the native
  // undo stack. Content is normalized as it enters (typing, paste, drop) so
  // this only has to run as a backstop once focus leaves.
  const applyNormalization = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const normalized = normalizeEditorHtml(editor.innerHTML);
    if (normalized === editor.innerHTML) return;

    editor.innerHTML = normalized;
    setContent(normalized);
  }, []);

  // Keeps saved/counted content normalized without touching the DOM mid-edit.
  const syncFromEditor = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    setContent(normalizeEditorHtml(editor.innerHTML));
  }, []);

  const execCommand = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    syncFromEditor();
  };

  const handleEditorKeyDown = (e) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;

    e.preventDefault();
    if (!insertLineBreak(editorRef.current)) return;
    syncFromEditor();
  };

  const insertNormalizedHtml = (transfer) => {
    const html = transfer.getData('text/html');
    const text = transfer.getData('text/plain');

    document.execCommand(
      'insertHTML',
      false,
      html ? normalizeEditorHtml(html) : plainTextToEditorHtml(text)
    );
    syncFromEditor();
  };

  const handleEditorPaste = (e) => {
    e.preventDefault();
    insertNormalizedHtml(e.clipboardData);
  };

  // Dropped content bypasses paste entirely and is the main way unsupported
  // markup (images especially) gets in, so it is normalized on arrival too.
  const handleEditorDrop = (e) => {
    e.preventDefault();
    insertNormalizedHtml(e.dataTransfer);
  };

  const handleInsertLink = () => {
    const url = prompt('Enter URL (http:// or https://):');
    if (url && /^https?:\/\//.test(url)) {
      execCommand('createLink', url);
    }
  };

  const handleFontSize = (e) => {
    execCommand('fontSize', e.target.value);
  };

  const handleFontFace = (e) => {
    execCommand('fontName', e.target.value);
  };

  const toggleHtmlMode = () => {
    if (htmlMode) {
      const html = normalizeEditorHtml(content);
      setContent(html);
      setHtmlMode(false);
      setTimeout(() => {
        if (editorRef.current) editorRef.current.innerHTML = html;
      }, 0);
    } else {
      if (editorRef.current) {
        setContent(normalizeEditorHtml(editorRef.current.innerHTML));
      }
      setHtmlMode(true);
    }
  };

  const handleSave = async () => {
    // `savedHtml` is exactly what the server will store, so the counter, this
    // check, and the request body can never disagree with each other.
    if (overLimit) {
      setMessage(`Profile too long (${currentLength}/${PROFILE_MAX_LENGTH})`);
      play({ id: 'error' });
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const res = await fetch('/api/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: savedHtml, bgcolor }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage(data.error);
        play({ id: 'error' });
        return;
      }
      setContent(data.profile);
      setBgcolor(data.bgcolor || '');
      if (!htmlMode && editorRef.current) {
        editorRef.current.innerHTML = data.profile;
      }
      setMessage('Profile saved!');
      play({ id: 'msgSnd' });
    } catch {
      setMessage('Failed to save.');
      play({ id: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  const profileBgStyle = bgcolor ? { backgroundColor: bgcolor } : {};

  return (
    <div className="buddy-info window">
      <div className="title-bar">
        <span className="title-bar-text">
          <span>Edit Profile: {user.screen_name}</span>
        </span>
        <div className="title-bar-controls">
          <button aria-label="Close" onClick={() => navigate('/')}></button>
        </div>
      </div>
      <div className="window-body">
        {/* Toolbar */}
        <div className="editor-toolbar" style={{ marginBottom: 4, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" onClick={() => execCommand('bold')} title="Bold" style={{ fontWeight: 'bold', minWidth: 24 }}>B</button>
          <button type="button" onClick={() => execCommand('italic')} title="Italic" style={{ fontStyle: 'italic', minWidth: 24 }}>I</button>
          <button type="button" onClick={() => execCommand('underline')} title="Underline" style={{ textDecoration: 'underline', minWidth: 24 }}>U</button>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button type="button" onClick={() => { setShowTextColor(!showTextColor); setShowBgColor(false); }} title="Font Color" style={{ minWidth: 24, fontSize: 12 }}>A▾</button>
            {showTextColor && (
              <ColorPalette
                title="Text Color"
                onSelect={(color) => execCommand('foreColor', color)}
                onClose={() => setShowTextColor(false)}
              />
            )}
          </div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button type="button" onClick={() => { setShowBgColor(!showBgColor); setShowTextColor(false); }} title="Background Color" style={{ minWidth: 24, fontSize: 10 }}>BG▾</button>
            {showBgColor && (
              <ColorPalette
                title="Background"
                onSelect={(color) => setBgcolor(color)}
                onClose={() => setShowBgColor(false)}
              />
            )}
          </div>
          {bgcolor && (
            <button type="button" onClick={() => setBgcolor('')} title="Clear Background" style={{ fontSize: 10, minWidth: 20, padding: '0 2px' }}>✕</button>
          )}
          <select onChange={handleFontFace} title="Font" defaultValue="" style={{ maxWidth: 90, fontSize: 11 }}>
            <option value="" disabled>Font</option>
            {FONTS.map((f) => (
              <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
            ))}
          </select>
          <select onChange={handleFontSize} title="Font Size" defaultValue="" style={{ width: 40 }}>
            <option value="" disabled>sz</option>
            {FONT_SIZES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button type="button" onClick={handleInsertLink} title="Insert Link" style={{ minWidth: 24 }}>🔗</button>
          <button type="button" onClick={toggleHtmlMode} title="Toggle HTML" style={{ minWidth: 40, fontSize: 10 }}>
            {htmlMode ? 'Rich' : 'HTML'}
          </button>
        </div>

        {/* Editor */}
        {htmlMode ? (
          <textarea
            className="profile"
            style={{ width: '100%', height: 300, fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        ) : (
          <div
            ref={editorRef}
            className="profile"
            contentEditable
            onKeyDown={handleEditorKeyDown}
            onPaste={handleEditorPaste}
            onDrop={handleEditorDrop}
            onInput={syncFromEditor}
            onBlur={applyNormalization}
            style={{ minHeight: 300, cursor: 'text', ...profileBgStyle }}
          />
        )}

        {/* Char counter */}
        <div style={{ fontSize: 11, textAlign: 'right', color: overLimit ? '#c00' : '#666', marginTop: 2 }}>
          {currentLength}/{PROFILE_MAX_LENGTH}
        </div>

        {/* Preview in HTML mode */}
        {htmlMode && (
          <div style={{ marginTop: 4 }}>
            <p style={{ fontSize: 11, margin: '2px 0' }}><strong>Preview:</strong></p>
            <div className="profile" style={{ height: 'auto', minHeight: 60, ...profileBgStyle }} dangerouslySetInnerHTML={{ __html: sanitizeProfile(content) }} />
          </div>
        )}

        {message && <p style={{ fontSize: 12, color: message.includes('saved') ? '#060' : '#c00' }}>{message}</p>}
        <div className="field-row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={handleSave} disabled={saving || overLimit}>
            {saving ? 'Saving...' : 'Update'}
          </button>
          <button onClick={() => navigate(`/profile/${encodeURIComponent(user.screen_name)}`)} style={{ marginLeft: 4 }}>
            View Profile
          </button>
        </div>
      </div>
    </div>
  );
}
